import { useState, useEffect } from "react";
import Navigation from "@/components/Navigation";
import OptimizedImage from "@/components/OptimizedImage";
import { supabase } from "@/integrations/supabase/client";

const PRP = () => {
  const [images, setImages] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchAndShuffleImages = async () => {
      try {
        console.log('PRP Page: Fetching images from clinical_images bucket...');
        const { data: files, error } = await supabase
          .storage
          .from('clinical_images')
          .list();

        if (error) {
          console.error('PRP Page: Error fetching images:', error);
          return;
        }

        if (!files || files.length === 0) {
          console.log('PRP Page: No images found in bucket');
          return;
        }

        console.log('PRP Page: Found files:', files);
        const imageUrls = files
          .filter(file => file.name.match(/\.(jpg|jpeg|png|gif|webp)$/i))
          .map(file => {
            const url = `${supabase.storage.from('clinical_images').getPublicUrl(file.name).data.publicUrl}`;
            console.log(`PRP Page: Generated URL for ${file.name}:`, url);
            return url;
          });

        // Shuffle the array of images
        const shuffledImages = [...imageUrls].sort(() => Math.random() - 0.5);
        console.log('PRP Page: Shuffled image URLs:', shuffledImages);
        setImages(shuffledImages);
      } catch (err) {
        console.error('PRP Page: Unexpected error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAndShuffleImages();
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation />
        <div className="pt-24 pb-16 px-6">
          <div className="animate-pulse space-y-8">
            <div className="h-64 bg-gray-200 rounded-lg"></div>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="h-48 bg-gray-200 rounded-lg"></div>
              <div className="h-48 bg-gray-200 rounded-lg"></div>
              <div className="h-48 bg-gray-200 rounded-lg"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      <div className="pt-24 pb-16 px-6">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-serif text-center mb-8">
            PRP Therapy
          </h1>
          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            <div>
              {images.length > 0 && (
                <OptimizedImage
                  src={images[0]}
                  alt="PRP Treatment"
                  className="rounded-lg shadow-lg w-full"
                  width={600}
                  height={400}
                  priority
                />
              )}
            </div>
            <div className="space-y-6">
              <p className="text-lg text-gray-700">
                Platelet-Rich Plasma (PRP) therapy is a cutting-edge regenerative treatment that uses your body's natural healing properties to stimulate hair growth and skin rejuvenation.
              </p>
              <p className="text-lg text-gray-700">
                By concentrating platelets from your own blood and reinjecting them into target areas, PRP therapy promotes healing, collagen production, and tissue regeneration.
              </p>
            </div>
          </div>
          
          <div className="space-y-8">
            <h2 className="text-3xl font-serif text-center mb-8">Benefits of PRP Therapy</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {images.slice(1, 4).map((image, index) => (
                <div key={index} className="relative">
                  <OptimizedImage
                    src={image}
                    alt={`PRP Benefit ${index + 1}`}
                    className="w-full h-64 object-cover rounded-lg shadow-lg"
                    width={400}
                    height={300}
                  />
                  <div className="p-6 bg-gray-50 rounded-lg mt-4">
                    <h3 className="text-xl font-semibold mb-4">
                      {index === 0 ? "Natural Healing" : 
                       index === 1 ? "Minimal Downtime" : 
                       "Long-lasting Results"}
                    </h3>
                    <p>
                      {index === 0 ? "Uses your body's own healing mechanisms for natural results" :
                       index === 1 ? "Quick recovery with little to no downtime after treatment" :
                       "Progressive improvement with long-lasting results"}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PRP;